/**
 * Header: navegación por hash + quién está logueado + estado de conexión.
 *
 * Dos indicadores que NO son lo mismo y por eso son dos chips distintos:
 *  - **conexión** (`GET /api/health`, siempre abierto): distingue "el API está
 *    caído" de "no hay partidos cargados" sin abrir la consola;
 *  - **identidad** (`GET /api/auth/me`): quién sos PARA EL API y con qué
 *    credencial. El dirigente tiene que poder ver de un vistazo si está
 *    entrando como él mismo o con el token compartido de la federación.
 */
import { useEffect, useState } from 'react';
import type { MessageKey, TFunction } from '../i18n/index';
import { useT } from '../i18n/use-t';
import type { ApiClient, Identity } from '../lib/api-client';
import { ApiError } from '../lib/api-client';
import {
  canImportDvw,
  canManageUsers,
  roleLabel,
  viaLabel,
  viaWarning,
  type IdentityStatus,
} from '../lib/auth';
import type { Route } from '../lib/router';

const LINKS: Array<{ hash: string; labelKey: MessageKey; match: Route['name'] }> = [
  { hash: '#/matches', labelKey: 'nav.matches', match: 'matches' },
  { hash: '#/competitions', labelKey: 'nav.competitions', match: 'competitions' },
  { hash: '#/season', labelKey: 'nav.season', match: 'season' },
  { hash: '#/planner', labelKey: 'nav.planner', match: 'planner' },
  { hash: '#/teams', labelKey: 'nav.teams', match: 'teams' },
  { hash: '#/settings', labelKey: 'nav.settings', match: 'settings' },
];

export type ConnectionState =
  | { kind: 'checking' }
  | { kind: 'ok'; version: string }
  | { kind: 'unauthorized' }
  | { kind: 'down'; message: string };

/** Chequeo de salud reutilizable (también lo usa el test de conexión de Ajustes). */
export function useHealth(client: ApiClient, baseUrl: string, token: string): ConnectionState {
  const [state, setState] = useState<ConnectionState>({ kind: 'checking' });

  useEffect(() => {
    let alive = true;
    setState({ kind: 'checking' });
    client
      .health()
      .then((health) => {
        if (alive) setState({ kind: 'ok', version: health.version });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') {
          setState({ kind: 'unauthorized' });
          return;
        }
        setState({ kind: 'down', message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      alive = false;
    };
    // El health se rehace cuando cambia a dónde/con qué apunta el portal.
  }, [client, baseUrl, token]);

  return state;
}

export interface IdentityState {
  status: IdentityStatus;
  identity: Identity | null;
}

/**
 * Quién soy, según el API (no según el localStorage).
 *
 * Se rehace cuando cambia la credencial: loguearse, desloguearse o pegar otro
 * Bearer en Ajustes actualizan el chip sin recargar la página.
 *
 * Un 401 es `anon` (no hay credencial válida). CUALQUIER otro fallo —red caída,
 * URL mal, un proxy que devuelve HTML— es `unknown`: no sabemos quién sos, pero
 * tampoco es cierto que no estés autenticado, así que el portal no bloquea.
 */
export function useIdentity(client: ApiClient, baseUrl: string, token: string): IdentityState {
  const [state, setState] = useState<IdentityState>({ status: 'checking', identity: null });

  useEffect(() => {
    let alive = true;
    setState({ status: 'checking', identity: null });
    client
      .me()
      .then((identity) => {
        if (alive) setState({ status: 'ok', identity });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const anon = err instanceof ApiError && err.kind === 'unauthorized';
        setState({ status: anon ? 'anon' : 'unknown', identity: null });
      });
    return () => {
      alive = false;
    };
  }, [client, baseUrl, token]);

  return state;
}

function ConnectionChip({ state, t }: { state: ConnectionState; t: TFunction }): JSX.Element {
  switch (state.kind) {
    case 'checking':
      return (
        <span className="chip muted">
          <span className="dot" /> {t('nav.connecting')}
        </span>
      );
    case 'ok':
      return (
        <span className="chip ok" title={t('nav.connectedTitle', { version: state.version })}>
          <span className="dot" /> {t('nav.connected')}
        </span>
      );
    case 'unauthorized':
      return (
        <span className="chip warn" title={t('nav.missingTokenTitle')}>
          <span className="dot" /> {t('nav.missingToken')}
        </span>
      );
    case 'down':
      return (
        <span className="chip bad" title={state.message}>
          <span className="dot" /> {t('nav.down')}
        </span>
      );
  }
}

/**
 * Quién está usando el portal. Con sesión muestra nombre + rol; con el Bearer
 * manual o el modo dev lo dice explícitamente (chip amarillo), porque "entrar
 * como la federación entera" no es lo mismo que entrar como uno.
 */
function IdentityChip({
  state,
  t,
  onLogout,
}: {
  state: IdentityState;
  t: TFunction;
  onLogout: (() => void) | null;
}): JSX.Element | null {
  if (state.status === 'checking') return null;

  if (state.identity === null) {
    // `unknown` (no se pudo preguntar) se muestra igual que anónimo, pero sin
    // dramatizar: el chip de conexión ya cuenta que el API no contesta.
    return (
      <a className="chip muted" href="#/login" title={t('auth.anonTitle')}>
        {t('auth.anon')} · {t('auth.login')}
      </a>
    );
  }

  const { identity } = state;
  const warning = viaWarning(identity.via, t);
  return (
    <span className={warning === null ? 'chip ok' : 'chip warn'} title={warning ?? undefined}>
      {t('auth.signedInAs', {
        name: identity.name,
        role: roleLabel(identity.role, t),
      })}
      {onLogout !== null ? (
        <>
          {' '}
          <button type="button" className="link" onClick={onLogout}>
            {t('auth.logout')}
          </button>
        </>
      ) : (
        <> · {viaLabel(identity.via, t)}</>
      )}
    </span>
  );
}

export function Header({
  route,
  connection,
  identity,
  apiLabel,
  onLogout,
}: {
  route: Route;
  connection: ConnectionState;
  identity: IdentityState;
  apiLabel: string;
  /** `null` cuando no hay sesión propia que cerrar (token manual o modo dev). */
  onLogout: (() => void) | null;
}): JSX.Element {
  const t = useT();
  // El padrón sólo se ofrece a quien lo puede usar: el API igual lo protege con
  // `users:manage`, pero un link que siempre rebota con 403 es ruido.
  const conImport = canImportDvw(identity.identity)
    ? [
        ...LINKS,
        {
          hash: '#/import',
          labelKey: 'import.navLabel' as MessageKey,
          match: 'import' as Route['name'],
        },
      ]
    : LINKS;
  const links = canManageUsers(identity.identity)
    ? [...conImport, { hash: '#/users', labelKey: 'users.navLabel' as MessageKey, match: 'unknown' as Route['name'] }]
    : conImport;

  return (
    <header className="nav">
      <div className="brand">
        {t('nav.brand')}
        <small>{t('nav.federation')}</small>
      </div>
      <nav className="nav-links">
        {links.map((link) => (
          <a
            key={link.hash}
            className={`nav-link${isActive(route, link.hash, link.match) ? ' active' : ''}`}
            href={link.hash}
          >
            {t(link.labelKey)}
          </a>
        ))}
      </nav>
      <div className="nav-status">
        <span className="hint" title={apiLabel}>
          {apiLabel}
        </span>
        <IdentityChip state={identity} t={t} onLogout={onLogout} />
        <ConnectionChip state={connection} t={t} />
      </div>
    </header>
  );
}

/**
 * Marca activo el link. Las rutas nuevas (`#/users`) todavía no las conoce
 * `parseHash`, así que caen en `unknown` y se comparan por el hash normalizado
 * (ver la nota de `authRoute` en `src/lib/auth.ts`).
 */
function isActive(route: Route, hash: string, match: Route['name']): boolean {
  return match === 'unknown' ? route.path === hash : route.name === match;
}
